import {
  findWordOccurrences,
  normalizeMatcherText
} from "../../game-core/src/matcher";
import {
  assertCanonicalString,
  assertScalarString,
  compareUtf8Bytes
} from "./encoding";
import { DealProtocolError, fail } from "./errors";
import type {
  CatalogValidationIssue,
  CatalogValidationResult,
  DealCardDefinition,
  DealCatalog,
  DealThemeDefinition,
  PlayerCount
} from "./types";

function issue(
  code: string,
  path: string,
  message: string
): CatalogValidationIssue {
  return { code, path, message };
}

function checkCanonical(
  value: string,
  path: string,
  issues: CatalogValidationIssue[]
): void {
  try {
    assertCanonicalString(value, path);
  } catch (error) {
    issues.push(
      issue(
        error instanceof DealProtocolError ? error.code : "NON_CANONICAL_STRING",
        path,
        error instanceof Error ? error.message : "Invalid canonical string"
      )
    );
  }
}

function checkScalar(
  value: string,
  path: string,
  issues: CatalogValidationIssue[]
): void {
  try {
    assertScalarString(value, path);
  } catch (error) {
    issues.push(
      issue(
        error instanceof DealProtocolError ? error.code : "INVALID_UNICODE_SCALAR",
        path,
        error instanceof Error ? error.message : "Invalid Unicode string"
      )
    );
  }
}

function validateCardDefinitions(
  theme: DealThemeDefinition,
  path: string,
  issues: CatalogValidationIssue[]
): void {
  const entries = Object.entries(theme.cards);
  if (entries.length !== 50) {
    issues.push(
      issue("INVALID_CARD_COUNT", `${path}.cards`, "Theme must define exactly 50 cards")
    );
  }

  for (const [key, card] of entries) {
    const cardPath = `${path}.cards.${key}`;
    checkCanonical(key, `${cardPath}.[key]`, issues);
    checkCanonical(card.cardId, `${cardPath}.cardId`, issues);
    checkScalar(card.displayText, `${cardPath}.displayText`, issues);
    if (key !== card.cardId) {
      issues.push(
        issue("CARD_KEY_MISMATCH", `${cardPath}.cardId`, "Card key and cardId differ")
      );
    }
    if (card.displayText.length === 0) {
      issues.push(
        issue("EMPTY_DISPLAY_TEXT", `${cardPath}.displayText`, "displayText is empty")
      );
    }
    if (card.cardKind !== "NORMAL" && card.cardKind !== "DUAL") {
      issues.push(
        issue("INVALID_CARD_KIND", `${cardPath}.cardKind`, "Unknown cardKind")
      );
    }
    if (card.matchForms.length === 0) {
      issues.push(
        issue("NO_MATCH_FORMS", `${cardPath}.matchForms`, "Card needs a match form")
      );
      continue;
    }

    let formsValid = true;
    for (const [formIndex, form] of card.matchForms.entries()) {
      const formPath = `${cardPath}.matchForms[${String(formIndex)}]`;
      checkScalar(form.text, `${formPath}.text`, issues);
      if (form.text.length === 0) {
        formsValid = false;
        issues.push(issue("EMPTY_MATCH_FORM", `${formPath}.text`, "Match form is empty"));
      }
      if (form.mode !== "SUBSTRING" && form.mode !== "WORD") {
        formsValid = false;
        issues.push(issue("UNKNOWN_MATCH_MODE", `${formPath}.mode`, "Unknown match mode"));
      }
    }

    if (formsValid && card.displayText.length > 0) {
      try {
        const standalone = findWordOccurrences(
          card.displayText,
          { "1": [card.cardId] },
          { [card.cardId]: card }
        );
        if (standalone.occurrences.length === 0) {
          issues.push(
            issue(
              "DISPLAY_TEXT_DOES_NOT_MATCH",
              cardPath,
              "displayText is not matched by this card"
            )
          );
        }
      } catch (error) {
        issues.push(
          issue(
            "INVALID_MATCH_FORM",
            cardPath,
            error instanceof Error ? error.message : "Matcher rejected card"
          )
        );
      }
    }
  }
}

function validatePacketsAndReferences(
  theme: DealThemeDefinition,
  path: string,
  issues: CatalogValidationIssue[]
): Map<string, (typeof theme.packets)[number]> {
  if (theme.packets.length !== 5) {
    issues.push(
      issue("INVALID_PACKET_COUNT", `${path}.packets`, "Theme must define five packets")
    );
  }

  const packets = new Map<string, (typeof theme.packets)[number]>();
  const referenceCounts = new Map<string, number>();
  const countReference = (cardId: string): void => {
    referenceCounts.set(cardId, (referenceCounts.get(cardId) ?? 0) + 1);
  };

  for (const [packetIndex, packet] of theme.packets.entries()) {
    const packetPath = `${path}.packets[${String(packetIndex)}]`;
    checkCanonical(packet.packetId, `${packetPath}.packetId`, issues);
    if (packets.has(packet.packetId)) {
      issues.push(
        issue("DUPLICATE_PACKET_ID", `${packetPath}.packetId`, "packetId is duplicated")
      );
    }
    packets.set(packet.packetId, packet);

    if (packet.initial.length !== 4) {
      issues.push(
        issue("INVALID_INITIAL_SIZE", `${packetPath}.initial`, "initial must have 4 cards")
      );
    }
    if (packet.penalty.length !== 5) {
      issues.push(
        issue("INVALID_PENALTY_SIZE", `${packetPath}.penalty`, "penalty must have 5 cards")
      );
    }

    const localIds = new Set<string>();
    let dualCount = 0;
    for (const [zone, cardIds] of [
      ["initial", packet.initial],
      ["penalty", packet.penalty]
    ] as const) {
      for (const [cardIndex, cardId] of cardIds.entries()) {
        const cardPath = `${packetPath}.${zone}[${String(cardIndex)}]`;
        checkCanonical(cardId, cardPath, issues);
        if (localIds.has(cardId)) {
          issues.push(issue("DUPLICATE_PACKET_CARD", cardPath, "Card repeats in packet"));
        }
        localIds.add(cardId);
        countReference(cardId);

        const card = theme.cards[cardId];
        if (card === undefined) {
          issues.push(issue("UNKNOWN_CARD", cardPath, `Unknown cardId: ${cardId}`));
          continue;
        }
        if (zone === "initial" && card.cardKind === "DUAL") {
          dualCount += 1;
        }
        if (zone === "penalty" && card.cardKind === "DUAL") {
          issues.push(issue("DUAL_IN_PENALTY", cardPath, "Penalty card cannot be DUAL"));
        }
      }
    }
    if (dualCount !== 1) {
      issues.push(
        issue(
          "INVALID_DUAL_COUNT",
          `${packetPath}.initial`,
          `Expected one DUAL card, found ${String(dualCount)}`
        )
      );
    }
  }

  if (theme.reserves.length !== 5) {
    issues.push(
      issue("INVALID_RESERVE_COUNT", `${path}.reserves`, "Theme must define 5 reserves")
    );
  }
  for (const [reserveIndex, cardId] of theme.reserves.entries()) {
    const reservePath = `${path}.reserves[${String(reserveIndex)}]`;
    checkCanonical(cardId, reservePath, issues);
    countReference(cardId);
    const card = theme.cards[cardId];
    if (card === undefined) {
      issues.push(issue("UNKNOWN_CARD", reservePath, `Unknown cardId: ${cardId}`));
    } else if (card.cardKind !== "NORMAL") {
      issues.push(issue("DUAL_IN_RESERVE", reservePath, "Reserve card must be NORMAL"));
    }
  }

  for (const cardId of Object.keys(theme.cards)) {
    const count = referenceCounts.get(cardId) ?? 0;
    if (count !== 1) {
      issues.push(
        issue(
          "CARD_REFERENCE_COUNT",
          `${path}.cards.${cardId}`,
          `Card must be referenced exactly once; found ${String(count)}`
        )
      );
    }
  }
  for (const cardId of referenceCounts.keys()) {
    if (theme.cards[cardId] === undefined) {
      issues.push(
        issue("UNKNOWN_CARD_REFERENCE", path, `Unknown referenced cardId: ${cardId}`)
      );
    }
  }

  return packets;
}

function occurrencesOverlap(
  left: { readonly span: { readonly start: number; readonly end: number } },
  right: { readonly span: { readonly start: number; readonly end: number } }
): boolean {
  return left.span.start < right.span.end && right.span.start < left.span.end;
}

function validateLineupMatcherConstraints(
  theme: DealThemeDefinition,
  cardIds: readonly string[],
  path: string,
  issues: CatalogValidationIssue[]
): void {
  const cards: Record<string, DealCardDefinition> = {};
  for (const cardId of cardIds) {
    const card = theme.cards[cardId];
    if (card !== undefined) {
      cards[cardId] = card;
    }
  }
  if (Object.keys(cards).length !== cardIds.length) {
    return;
  }

  const displays = new Map<string, string>();
  const forms = new Map<string, string>();
  for (const card of Object.values(cards)) {
    try {
      const display = normalizeMatcherText(card.displayText);
      const previousDisplay = displays.get(display);
      if (previousDisplay !== undefined && previousDisplay !== card.cardId) {
        issues.push(
          issue(
            "DUPLICATE_DISPLAY_TEXT",
            path,
            `Cards ${previousDisplay} and ${card.cardId} normalize to the same displayText`
          )
        );
      }
      displays.set(display, card.cardId);

      for (const form of card.matchForms) {
        const normalizedForm = normalizeMatcherText(form.text);
        const previousForm = forms.get(normalizedForm);
        if (previousForm !== undefined && previousForm !== card.cardId) {
          issues.push(
            issue(
              "DUPLICATE_MATCH_FORM",
              path,
              `Cards ${previousForm} and ${card.cardId} share a normalized match form`
            )
          );
        }
        forms.set(normalizedForm, card.cardId);
      }
    } catch {
      return;
    }
  }

  const overlapPairs = new Set<string>();
  for (const sourceCard of Object.values(cards)) {
    for (const sourceForm of sourceCard.matchForms) {
      let occurrences;
      try {
        occurrences = findWordOccurrences(
          sourceForm.text,
          { "1": cardIds },
          cards
        ).occurrences;
      } catch {
        return;
      }
      for (let leftIndex = 0; leftIndex < occurrences.length; leftIndex += 1) {
        const left = occurrences[leftIndex]!;
        for (
          let rightIndex = leftIndex + 1;
          rightIndex < occurrences.length;
          rightIndex += 1
        ) {
          const right = occurrences[rightIndex]!;
          if (left.cardId === right.cardId || !occurrencesOverlap(left, right)) {
            continue;
          }
          const pair = [left.cardId, right.cardId].sort(compareUtf8Bytes).join("|");
          if (!overlapPairs.has(pair)) {
            overlapPairs.add(pair);
            issues.push(
              issue(
                "OVERLAPPING_MATCH_FORMS",
                path,
                `Cards ${pair} produce overlapping Matcher occurrences`
              )
            );
          }
        }
      }
    }
  }
}

function validateLineups(
  theme: DealThemeDefinition,
  packets: ReadonlyMap<string, DealThemeDefinition["packets"][number]>,
  path: string,
  issues: CatalogValidationIssue[]
): void {
  const validCounts = new Set<PlayerCount>([3, 4, 5]);
  for (const key of Object.keys(theme.allowedLineups)) {
    const numeric = Number(key);
    if (!validCounts.has(numeric as PlayerCount)) {
      issues.push(
        issue("INVALID_LINEUP_COUNT_KEY", `${path}.allowedLineups.${key}`, "Invalid count")
      );
    }
  }

  const nonEmptyCounts = new Set<PlayerCount>();
  const lineupIds = new Set<string>();
  for (const playerCount of [3, 4, 5] as const) {
    const lineups = theme.allowedLineups[playerCount] ?? [];
    const lineupCombinations = new Set<string>();
    if (lineups.length > 0) {
      nonEmptyCounts.add(playerCount);
    }
    for (const [lineupIndex, lineup] of lineups.entries()) {
      const lineupPath = `${path}.allowedLineups.${String(playerCount)}[${String(lineupIndex)}]`;
      checkCanonical(lineup.lineupId, `${lineupPath}.lineupId`, issues);
      if (lineupIds.has(lineup.lineupId)) {
        issues.push(
          issue("DUPLICATE_LINEUP_ID", `${lineupPath}.lineupId`, "lineupId is duplicated")
        );
      }
      lineupIds.add(lineup.lineupId);
      const combinationKey = lineup.packetIds.join("\0");
      if (lineupCombinations.has(combinationKey)) {
        issues.push(
          issue(
            "DUPLICATE_LINEUP_COMBINATION",
            `${lineupPath}.packetIds`,
            "The same packet combination is published more than once"
          )
        );
      }
      lineupCombinations.add(combinationKey);
      if (lineup.packetIds.length !== playerCount) {
        issues.push(
          issue(
            "INVALID_LINEUP_SIZE",
            `${lineupPath}.packetIds`,
            `Expected ${String(playerCount)} packetIds`
          )
        );
      }

      const seenPackets = new Set<string>();
      const cardIds: string[] = [];
      for (const [packetIndex, packetId] of lineup.packetIds.entries()) {
        const packetPath = `${lineupPath}.packetIds[${String(packetIndex)}]`;
        checkCanonical(packetId, packetPath, issues);
        if (seenPackets.has(packetId)) {
          issues.push(issue("DUPLICATE_LINEUP_PACKET", packetPath, "Packet repeats"));
        }
        seenPackets.add(packetId);
        if (
          packetIndex > 0 &&
          compareUtf8Bytes(lineup.packetIds[packetIndex - 1]!, packetId) >= 0
        ) {
          issues.push(
            issue("UNSORTED_LINEUP_PACKETS", packetPath, "packetIds must be strictly sorted")
          );
        }
        const packet = packets.get(packetId);
        if (packet === undefined) {
          issues.push(issue("UNKNOWN_PACKET", packetPath, `Unknown packetId: ${packetId}`));
        } else {
          cardIds.push(...packet.initial, ...packet.penalty);
        }
      }
      if (cardIds.length === playerCount * 9) {
        validateLineupMatcherConstraints(theme, cardIds, lineupPath, issues);
      }
    }
  }

  const supported = new Set(theme.supportedPlayerCounts);
  if (supported.size !== theme.supportedPlayerCounts.length) {
    issues.push(
      issue("DUPLICATE_SUPPORTED_COUNT", `${path}.supportedPlayerCounts`, "Count repeats")
    );
  }
  for (const count of supported) {
    if (!validCounts.has(count)) {
      issues.push(
        issue("INVALID_SUPPORTED_COUNT", `${path}.supportedPlayerCounts`, "Invalid count")
      );
    }
  }
  const sameCounts =
    supported.size === nonEmptyCounts.size &&
    [...supported].every((count) => nonEmptyCounts.has(count));
  if (!sameCounts) {
    issues.push(
      issue(
        "SUPPORTED_COUNT_MISMATCH",
        `${path}.supportedPlayerCounts`,
        "supportedPlayerCounts must equal the non-empty lineup keys"
      )
    );
  }
}

export function validateCatalog(catalog: DealCatalog): CatalogValidationResult {
  const issues: CatalogValidationIssue[] = [];
  checkCanonical(catalog.catalogRevision, "catalogRevision", issues);

  const themeKeys = new Set<string>();
  for (const [themeIndex, theme] of catalog.themes.entries()) {
    const path = `themes[${String(themeIndex)}]`;
    checkCanonical(theme.themeId, `${path}.themeId`, issues);
    checkCanonical(theme.themeRevision, `${path}.themeRevision`, issues);
    checkCanonical(theme.compatibleRulesVersion, `${path}.compatibleRulesVersion`, issues);
    checkCanonical(theme.compatibleDealVersion, `${path}.compatibleDealVersion`, issues);
    checkCanonical(theme.compatibleMatcherVersion, `${path}.compatibleMatcherVersion`, issues);

    const themeKey = `${theme.themeId}\0${theme.themeRevision}`;
    if (themeKeys.has(themeKey)) {
      issues.push(issue("DUPLICATE_THEME", path, "themeId/themeRevision tuple repeats"));
    }
    themeKeys.add(themeKey);

    validateCardDefinitions(theme, path, issues);
    const packets = validatePacketsAndReferences(theme, path, issues);
    validateLineups(theme, packets, path, issues);
  }

  return { valid: issues.length === 0, issues };
}

export function assertValidCatalog(catalog: DealCatalog): void {
  const result = validateCatalog(catalog);
  if (!result.valid) {
    const summary = result.issues
      .slice(0, 20)
      .map((entry) => `${entry.path}: ${entry.code}`)
      .join(", ");
    fail("INVALID_CATALOG", summary || "Catalog validation failed");
  }
}
